import { FlatLookupView } from "@/Common/Api/MedfarDictionaries/Dto/Responses/FlatLookupView";
import { ComboBoxDropDownItem } from "@/Common/Components/Inputs/Combobox";
import { ArrayHelper } from "@/Common/Helpers/ArrayHelper";
import { DateTime } from "@/Common/Helpers/DateTime";
import { PortalHelper } from "@/Common/Helpers/PortalHelper";
import { EmployeeType } from "@/Common/Models/Employee/Enums/EmployeeType";
import { EditMode, HistoryTab, MedicalItemsTab, PatientMedicationAction, PatientTab, ProfileTab } from "@/Common/Models/Patient/PatientEnums";
import { Person } from "@/Common/Models/Person/Person";
import { SortDirection } from "@/Common/Models/UI/Enums";
import { CheckboxTarget } from "@/Common/Models/UI/Inputs/CheckboxTarget";
import { ICheckable } from "@/Common/Models/UI/Inputs/ICheckable";
import { IJPatientAppointmentModel as JSONInterfacesPatientAppointmentIJPatientAppointmentModel } from "@/Patient/Models/JSONInterfaces/Appointment/IJPatientAppointmentModel";
import { IJPatientAppointmentModel as JSONInterfacesPatientIJPatientAppointmentModel } from "@/Patient/Models/JSONInterfaces/History/IJPatientAppointmentModel";
import { IJPatientPrivateBillModel } from "@/Patient/Models/JSONInterfaces/History/IJPatientPrivateBillModel";
import { IJPatientRecallsModel } from "@/Patient/Models/JSONInterfaces/History/IJPatientRecallsModel";
import { IJPatientTabModel } from "@/Patient/Models/JSONInterfaces/IJPatientTabModel";
import { IJPrivateBillModel } from "@/Patient/Models/JSONInterfaces/IJPrivateBillModel";
import { IJConsultationRequestModel } from "@/Patient/Models/JSONInterfaces/IJReferralModel";
import { IJPatientMedicalItemsModel } from "@/Patient/Models/JSONInterfaces/MedicalItems/IJPatientMedicalItemsModel";
import { IJPatientMedicalNoteModel } from "@/Patient/Models/JSONInterfaces/MedicalNote/IJPatientMedicalNoteModel";
import { IJPatientMedicalSummaryModel } from "@/Patient/Models/JSONInterfaces/MedicalSummary/IJPatientMedicalSummaryModel";
import { IJMedicationTabModel } from "@/Patient/Models/JSONInterfaces/Medication/IJMedicationTabModel";
import { IJPatientMedicationGridModel } from "@/Patient/Models/JSONInterfaces/Medication/IJPatientMedicationGridModel";
import { Encounter } from "@/Patient/Models/MedicalSummary/Encounter";
import { MedicationModel as ModelsPatientMedicationModel } from "@/Patient/Models/Medication/MedicationModel";
import { IPatientCommunicationsModel } from "@/Patient/Models/UI/History/IPatientCommunicationsModel";
import { IPatientMessagesModel } from "@/Patient/Models/UI/History/IPatientMessagesModel";
import { IPatientTasksModel } from "@/Patient/Models/UI/History/IPatientTasksModel";
import { MedicalNoteViewMode } from "@/Patient/Models/UI/MedicalNote/Enums/MedicalNote";
import { IPatientMedicalSummaryModel } from "@/Patient/Models/UI/MedicalSummary/IPatientMedicalSummaryModel";

class PatientModelHelper {
    public static setDefaultHistoryMessageModel(): IPatientMessagesModel {
        const obj = {
            SelectedMessageFrom: "",
            SelectedMessageTo: "",
            SelectedMessageSubject: "",
            SelectedMessageBody: "",
            MessageList: [],
            ShowDetails: false,
            SearchText: "",
            DateFrom: null,
            DateTo: null,
            IsCriteriaExpanded: false,
            UnreadOnly: false,
            Notes: []
        };

        const model: IPatientMessagesModel = Immutable.fromJS(obj);
        return model;
    }

    public static setDefaultHistoryTaskModel(): IPatientTasksModel {
        const obj = {
            TaskList: [],
            SearchText: "",
            FromEmployee: "",
            ToEmployeeWithStatus: [],
            Subject: "",
            StartDate: "",
            DueDate: "",
            Priority: "",
            Type: "",
            Body: "",
            ShowDetails: false,
            DateFrom: null,
            DateTo: null,
            IsCriteriaExpanded: false,
            NotCompletedOnly: false,
            Notes: []
        };

        const model: IPatientTasksModel = Immutable.fromJS(obj);
        return model;
    }

    public static setDefaultHistoryCommunicationsModel(): IPatientCommunicationsModel {
        const obj = {
            CommunicationsList: [],
            ShowNewCommunicationPopup: false,
            Statuses: PatientModelHelper.getStatusesLookupsFromCache(),
            SelectedStatusId: cacheStore.FindLookupByLookupCode("RFBooked").LookupID,
            Note: ""
        };

        const model: IPatientCommunicationsModel = Immutable.fromJS(obj);
        return model;
    }

    public static getLatestUnapprovedEncounter(patientMedicalSummaryModel: IPatientMedicalSummaryModel): Encounter | undefined {
        return patientMedicalSummaryModel
            .get("Encounters")
            .find(encounter => !encounter.IsSignedOff);
    }

    public static getStatusesLookupsFromCache(): FlatLookupView[] {
        let priorityLookups = cacheStore.FindLookupsByTypeCode("RecallStatusType");
        if (!cacheStore.ClinicSettings.IsCallCenterEnabled()) {
            priorityLookups = priorityLookups
                .filter(
                    lookup => lookup.LookupCode != "IncomingCall"
                    && lookup.LookupCode != "OutgoingCall"
                );
        }
        priorityLookups = ArrayHelper.SortDynamic(priorityLookups, "LookupPosition");
        return priorityLookups;
    }

    public static getDefaultHistoryPatientAppointmentModel(): JSONInterfacesPatientIJPatientAppointmentModel {
        return {
            SearchText: "",
            IsCriteriaExpanded: false,
            ShowDetails: false,
            DateFrom: null,
            DateTo: null,
            AppointmentList: [],
            Subject: "",
            SiteName: "",
            SiteId: "",
            With: "",
            Room: "",
            StartTime: null,
            StartDateForPrint: null,
            EndTime: null,
            StateName: "",
            StateId: "",
            Notes: "",
            Paid: "",
            IsPaid: false,
            SelectedAppointmentStateCode: "",
            SelectedAppointmentStateId: "",
            AppointmentStates: [],
            NotesForEdit: false,
            OpenModifyPopup: false,
            SelectedAppointmentId: "",
            ShowOnlyCurrent: false,
            SelectedSiteId: "",
            Sites: [],
            IsComboAppointmentChangePopupVisible: false,
            IsTeleconsultation: false,
            IsReadOnly: false
        };
    }

    public static getDefaultPatientAppointmentModel(): JSONInterfacesPatientAppointmentIJPatientAppointmentModel {
        return {
            ShowManagement: false,
            ShowSavePredefinedAppointment: false,
            IsClinicianFilterTab: true,
            IsSchedulerInStackMode: false,
            CliniciansSearchText: "",
            CliniciansGlobalCache: [],
            CliniciansFiltered: [],
            ServiceForSpecificClinician: [],
            IsAllServiceSelected: false,
            IsOtherServiceSelected: false,
            ServicesSearchText: "",
            ServicesGlobalCache: [],
            ServicesFiltered: [],
            ServicesClinicianList: [],
            IsAllEmployeeSelected: false,
            SearchCriteria: [],
            TimeSlot: [],
            Notes: "",
            ReferalPhoneNumber: "",
            ReferalDoctorSearchString: "",
            SelectedReferalDoctor: [],
            SelectedReferalDoctorId: "",
            SelectedAppointmentStateCode: "",
            AppointmentStates: [],
            SavingAppointmentsList: {},
            SendingAppointmentsList: [],
            RawAppointmentData: [],
            TempPredefinedList: [],
            PreviousFirstDate: [],
            MondayCheck: true,
            TuesdayCheck: true,
            WednesdayCheck: true,
            ThursdayCheck: true,
            FridayCheck: true,
            WeekdayCheck: true,
            WeekendCheck: true,
            PMCheck: true,
            AMCheck: true,
            IsInEditMode: false,
            DefaultServices: [],
            DefaultSubjects: [],
            Subject: "",
            SelectedSubjectResourceDefinitionId: "",
            ShowAlgoOptPopup: false,
            GlobalServiceOrComboList: [],
            GlobalServiceOrComboFilteredList: [],
            EmployeeServiceOrComboList: [],
            AnyServiceList: [],
            PredefinedAppointmentSaveState: null
        };
    }

    public static getDefaultPatientPrivateBillModel(clinicianList: Person[]): IJPatientPrivateBillModel {
        return {
            SearchText: "",
            DateFrom: null,
            DateTo: null,
            IsCriteriaExpanded: false,
            PrivateBillList: [],
            ShowDetailsOrNewBill: false,
            IsNewBill: false,
            IsSave: true,
            PrivateBillId: "",
            CreatedBy: "",
            Provider: "",
            BillNumber: "",
            SiteName: "",
            SelectedBillSiteId: sessionStore.getCurrentEmployee().getActiveSite().Model.Id,
            BillDate: null,
            BillDateForHeader: "",
            PaymentMethod: "",
            Notes: "",
            GST: 0,
            QST: 0,
            HST: 0,
            GenericTax: 0,
            Subtotal: 0,
            TotalAmount: 0,
            Discount: 0,
            DiscountType: "",
            GSTTotal: 0,
            QSTTotal: 0,
            HSTTotal: 0,
            GenericTaxTotal: 0,
            Total: 0,
            BilledService: [],
            IsInvalid: false,
            InvalidatedBy: "",
            InvalidDate: null,
            InvalidNote: "",
            IsInvalidPopupOpen: false,
            InvalidationNoteForSave: "",
            DateOfVisit: DateTime.nowDeprecated(),
            SelectedSiteId: "",
            Clinicians: clinicianList,
            SelectedClinicians: [],
            SelectedPaymentMethod: "",
            CancelPopupIsOpen: false,
            NewBillSubtotal: 0,
            GSTPercent: 0,
            QSTPercent: 0,
            HSTPercent: 0,
            GenericTaxPercent: 0,
            NewBillGSTTotal: 0,
            NewBillQSTTotal: 0,
            NewBillHSTTotal: 0,
            NewBillGenericTaxTotal: 0,
            NewBillTotal: 0,
            NewBillDiscount: 0,
            IsCashSelected: true,
            SelectedClinicianName: "",
            NewBillNotes: "",
            NewBillTotalAmount: 0,
            CostCenterList: [],
            SelectedCostCenterId: "",
            IsSaveLocked: false,
            IsPortalSyncPopupOpen: false,
            PortalLinkedItemModel: null,
            IsEditBill: false,
            IsFromNote: false,
            IsProviderLocked: false,
            BillFromListForSend: null
        };
    }

    public static getDefaultPatientRecallsModel(): IJPatientRecallsModel {
        return {
            SearchText: "",
            RecallsList: [],
            Description: "",
            Provider: "",
            Speciality: "",
            DueDate: null,
            CreatedBy: "",
            Priority: "",
            Notes: "",
            ShowDetails: false,
            RecallCommunication: [],
            DateFrom: null,
            DateTo: null,
            IsCriteriaExpanded: false,
            IsCompleted: false,
            SelectedRecallId: "",
            ShowNewEntryPopup: false,
            SelectedStatusId: "",
            Statuses: [],
            Note: "",
            ShowOnlyNotCompleted: false
        };
    }

    public static getDefaultPatientUIModel(): IJPatientTabModel {
        var canSeePatientMedicalInfo = sessionStore.hasRightsForAction([EmployeeType.Practician]);

        var patientModel: IJPatientTabModel = {
            PatientContactId: "",
            PatientContactFacetId: "",
            PatientLastName: "",
            PatientFirstName: "",
            PatientPreferredName: "",
            PatientPreferredPronouns: 0,
            PatientSpecifiedPreferredPronouns: "",
            GenderIdentity: 11,
            ActiveVerticalTab: canSeePatientMedicalInfo ? PatientTab.MedicalSummary : PatientTab.Profile,
            ReturnToView: null,
            PatientBannerFocusOperation: null,
            ShowProfileImagePopup: false,
            LockPatientDemographicSaveButton: false,
            ShowProfileMemoPopup: false,
            IsProfileMemoEditMode: false,
            PatientProfileActiveTab: ProfileTab.Demographic,
            PatientProfileEditMode: EditMode.View,
            PatientHistoryActiveTab: HistoryTab.Appointment,
            PatientHistoryEditMode: EditMode.View,
            PatientMedicalItemsTab: MedicalItemsTab.NONE,
            ShowDeactivatePopup: false,
            DeactivationNote: "",
            SelectedDeactivationReasonLookupId: "",
            AvailableDeactivationReasonLookupIds: []
        };

        return patientModel;
    }

    public static getDefaultPrivateBillModel(): IJPrivateBillModel {
        var model: IJPrivateBillModel =
        {
            SearchText: "",
            DateFrom: null,
            DateTo: null,
            IsCriteriaExpanded: false,
            PrivateBillList: Immutable.List([]),
            ShowDetailsOrNewBill: false,
            IsNewBill: false,
            IsSave: false,
            PrivateBillId: "",
            CreatedBy: "",
            Provider: "",
            BillNumber: "",
            SiteName: "",
            SelectedBillSiteId: sessionStore.getCurrentEmployee().getActiveSite().Model.Id,
            BillDateRaw: null,
            BillDate: "",
            BillDateForHeader: "",
            PaymentMethod: "",
            Notes: "",
            Subtotal: 0,
            Discount: 0,
            DiscountType: "",
            GSTTotal: 0,
            GST: 0,
            QST: 0,
            QSTTotal: 0,
            Total: 0,
            BilledService: Immutable.List([]),
            IsInvalid: 0,
            InvalidatedBy: "",
            InvalidNote: "",
            IsInvalidPopupOpen: false,
            InvalidationNoteForSave: "",
            PortalLinkedItemModel: Immutable.fromJS(PortalHelper.getDefaultPortalLinkedItemModel()),
            IsPortalSyncPopupOpen: false,
            Clinicians: Immutable.List([]),
            SelectedClinicians: Immutable.List([]),
            Sites: Immutable.List(cacheStore.GetSites(null)),
            IsCashSelected: true,
            IsClosed: false,
            IsEditBill: false,
            EmployeeContactFacetId: "",
            IsFromNote: false,
            IsProviderLocked: false
        };

        return model;
    }

    public static getDefaultConsultationRequestModel(): IJConsultationRequestModel {
        var model: IJConsultationRequestModel = {
            ConsultationRequestId: "",
            IsSignedOff: false,
            SiteStreetAddress: "",
            SiteZipCode: "",
            SiteCity: "",
            SiteName: "",
            SitePhoneNumber: "",
            SiteFax: "",
            SiteCountry: "",
            ConsultationRequestStatus: "",
            ConsultationRequestSpeciality: "",
            ConsultationRequestDate: "",
            ConsultationRequestDescription: "",
            ConsultationRequestDoctor: "",
            ConsultationRequestIsUrgent: false,
            ConsultationRequestRecipient: "",
            DoctorLicenseNumber: "",
            PrintedDate: "",
            EmptySpace: "",
            PatientId: "",
            PatientName: "",
            PatientPhoneNumber: "",
            PatientFileNumber: "",
            PatientNAM: "",
            PatientGender: "",
            PatientDateOfBirth: "",
            PatientPhone: "",
            AvailableSpecialities: [],
            SelectedSupervisorName: "",
            SelectedSupervisorContactId: ""
        };

        return model;
    }

    public static getDefaultPatientMedicalItemsModel(): IJPatientMedicalItemsModel {
        var detailModel: IJPatientMedicalItemsModel =
        {
            SearchTerm: "",
            ItemView: null,
            EMRTemplateList: [],
            SelectedEMRTemplateId: "",
            MedicalItemsList: [],
            AllMedicalItemsList: [],
            EMRTemplateComments: {},
            EMRTemplateValidationNotes: {},
            CommentText: "",
            ValidationNoteText: "",
            IsInCommentEdit: false,
            IsInValidationEdit: false,
            ShowDeleteMedicalItemConfirmation: false,
            MedicalItemToDelete: null,
            IsSavePlusActivated: false,
            ItemsToBeSaved: [],
            IsEditingFromAssessment: false,
            ActiveMedicationEditBaseMedicalItem: null,
            ActiveMedicationEditNewMedicalItem: null
        };

        return detailModel;
    }

    public static getDefaultPatientMedicalNoteModel(): IJPatientMedicalNoteModel {
        var medicalnotemodel: IJPatientMedicalNoteModel =
        {
            PatientContactId: "",
            PatientInactivableId: "",
            Encounter: null,
            EncounterNotes: [],
            MedicalItemsAlertAndSpecialNeed: [],
            MedicalItemsFamilyHistory: [],
            MedicalItemsImmunization: [],
            MedicalItemsMedicalReaction: [],
            MedicalItemsMedicationAndPrescription: [],
            FilteredMedicalItemsMedicationAndPrescription: [],
            MedicalItemsRiskFactor: [],
            MedicalItemsVitalSign: [],
            MedicalItemsProblemsAndDiagnostics: [],
            PrivateBills: [],
            ConsultationRequest: [],
            Requests: [],
            HandwrittenNotes: [],
            IsMedicalSummarySectionExpanded: true,
            IsProgressNoteSectionExpanded: true,
            IsFormsSectionExpanded: true,
            IsAssesmentSectionExpanded: true,
            IsPlanSectionExpanded: true,
            IsClinicalFormsExpanded: true,
            ViewMode: MedicalNoteViewMode.View,
            LeftMenuOpen: true,
            IsNotePopupOpen: false,
            NotePopupContext: "",
            NotePopupLeft: 0,
            NotePopupTop: 0,
            EncounterMedfarBillsLight: [],
            EncounterMedfarBills: [],
            ShowNewMedfarBillPopup: false,
            MedfarBillModel: null,
            IsRISRequestPopupOpen: false,
            ConsultationRequestPopupModel: Immutable.fromJS(PatientModelHelper.getDefaultConsultationRequestModel()),
            IsConsultationRequestFaxPopupOpen: false,
            ShowCurrentPrescriptionFaxPopup: false,
            PrintCurrentPrescription: false,
            SignCurrentPrescription: false,

            PrintCurrentRequest: false,
            SignCurrentRequest: false,

            IsPrivateBillPopupOpen: false,
            IsPrivateBillEditPopupOpen: false,
            PrivateBillPopupModel: Immutable.fromJS(PatientModelHelper.getDefaultPrivateBillModel()),
            PrivateBillBillableServices: [],
            IsTaskMessagePopupInTaskMode: false,
            ReasonClickableTextVisible: false,
            HistoryClickableTextVisible: false,
            ExamenClickableTextVisible: false,
            LabClickableTextVisible: false,
            ImagingClickableTextVisible: false,
            OtherNotesClickableTextVisible: false,
            FollowUpPlanClickableTextVisible: false,
            IsEditConsultationDetailsPopupVisible: false,
            ConcurrentEditSignedOffBy: "",
            ConcurrentEditSignedOffDate: null,
            ConcurrentEditShowWarningPopup: false,
            ConcurrenEditIsForMedicalItem: false,
            AlreadySignedOffBy: "",
            AlreadySignedOffDate: null,
            AlreadySignedOffShowWarningPopup: false,
            ScrollPosition: 0,
            AutosaveFailed: false,
            AutosaveFailedSignoffDate: null,
            AutosaveFailedSignoffUser: "",

            SpecialityMedfarBillingPopup: false,
            IsMedfarBillingSpecialist: false,

            ShowApprovalSupervisorPopup: false,
            SupervisorName: "",
            PendingApprovalNotes: [],
            PendingApprovalStatusInt: 0,
            IsSupervisorLocked: false,

            ShowReusingBillPopup: false,
            ReusingPrivateBillsList: [],
            ShowReusingPrivateBillCriteria: false,
            ReusingPrivateBillCriteriaFrom: null,
            ReusingPrivateBillCriteriaTo: null,
            ReusingPrivateBillSearchText: "",
            SelectedReusedPrivateBillFileName: "",
            SelectedReusedPrivateBillId: "",
            ShowNoBillsPopup: false,

            IndividualTaskLinkedItems: [],
            AreAllMessageRead: false,
            AreAllTasksCompleted: false,
            IndividualMessageLinkedItems: [],
            IndividualRecallLinkedItems: [],
            AreAllRecallsCompleted: false,
            PortalLinkedItemModel: Immutable.fromJS(PortalHelper.getDefaultPortalLinkedItemModel())
        };

        return medicalnotemodel;
    }

    public static getDefaultPatientSummaryModel(): IJPatientMedicalSummaryModel {
        var patientModel: IJPatientMedicalSummaryModel =
        {
            PatientContactId: "",
            PatientInactivableId: "",
            AreEncountersLoaded: false,
            AreLookupsResolved: false,
            IsSearchCriteriaExpanded: false,
            IsDisplayingOnlyUnsigned: false,
            IsDisplayOnlyClinicVisitNotes: false,
            EncounterSearchTerm: "",
            EncounterSearchDateFrom: null,
            EncounterSearchDateTo: null,
            DisplayedEncounters: [],
            Encounters: [],
            EncountersSearchStrings: {},
            MedicalItemsAlertAndSpecialNeed: [],
            MedicalItemsFamilyHistory: [],
            MedicalItemsImmunization: [],
            MedicalItemsMedicalReaction: [],
            MedicalItemsActiveMedication: [],
            MedicalItemsMedicationAndPrescription: [],
            FilteredMedicalItemsMedicationAndPrescription: [],
            Prescriptions: [],
            MedicalItemsRiskFactor: [],
            ImmunizationMedicalItems: [],
            MedicalItemsVitalSign: [],
            MedicalItemsScreeningTest: [],
            MedicalItemsProblemsOnGoing: [],
            MedicalItemsProblemsHistory: [],
            MedicalItemsSurgeryHistory: [],
            MedicalItemsPsychiatricHistory: [],
            MedicalItemsGynecologicalHistory: [],
            EMRTemplateValidationNotes: {},
            EMRTemplateComments: {},
            AlertToRemoveEMRTemplateIds: [],
            AlertsToDisplay: [],
            ShowAlertPopup: false,
            IsTabActive: false,
            ShowNewNoteWarning: false,
            ShowNoteCreationSpecialityErrorWarning: false,
            NewNoteWarningEncounterDate: null,
            NewNoteWarningEncounterId: "",
            ObstetricalSummary: null
        };

        return patientModel;
    }

    public static getDefaultPatientMedicationTabModel(): IJMedicationTabModel {
        const actions = [
            PatientMedicationAction.CompleteAndReplace,
            PatientMedicationAction.AugmentAndReplace,
            PatientMedicationAction.ReduceAndReplace,
            PatientMedicationAction.MarkAsCancelled,
            PatientMedicationAction.MarkAsInterrupted,
            PatientMedicationAction.MarkAsSuspended,
            PatientMedicationAction.MarkAsCompleted
        ].map((action, index) =>
            new ComboBoxDropDownItem(
                action,
                ModelsPatientMedicationModel.PatientMedicationActionFromEnum(action),
                index,
                null
            ));
        return {
            SearchPrescriptionTextBox: "",
            ShowUpdateMedicationPopup: false,
            MedicationModel: null,
            UpdateMedicationStatuses: Immutable.List(actions),
            DefaultMedicationAction: PatientMedicationAction.CompleteAndReplace
        };
    }

    public static getPatientMedicationGridModel(): IJPatientMedicationGridModel {
        var statuses: ICheckable[] = [];
        var origins: ICheckable[] = [];
        var details: ICheckable[] = [];

        var statusActive: ICheckable = {
            Id: CheckboxTarget.Myle_PatientCPP_MedicationSearch_Status_ActiveCheck.toString(),
            Checked: true,
            Target: CheckboxTarget.Myle_PatientCPP_MedicationSearch_Status_ActiveCheck,
            Name: cacheStore.GetString("Active2Label"),
            Visible: true
        };
        statuses.push(statusActive);
        var statusExpired: ICheckable = {
            Id: CheckboxTarget.Myle_PatientCPP_MedicationSearch_Status_ExpiredCheck.toString(),
            Checked: true,
            Target: CheckboxTarget.Myle_PatientCPP_MedicationSearch_Status_ExpiredCheck,
            Name: cacheStore.GetString("ExpiredLabel"),
            Visible: true
        };
        statuses.push(statusExpired);
        var statusCeased: ICheckable = {
            Id: CheckboxTarget.Myle_PatientCPP_MedicationSearch_Status_CeasedCheck.toString(),
            Checked: false,
            Target: CheckboxTarget.Myle_PatientCPP_MedicationSearch_Status_CeasedCheck,
            Name: cacheStore.GetString("CeasedLabel"),
            Visible: true
        };
        statuses.push(statusCeased);
        var statusCompleted: ICheckable = {
            Id: CheckboxTarget.Myle_PatientCPP_MedicationSearch_Status_CompletedCheck.toString(),
            Checked: false,
            Target: CheckboxTarget.Myle_PatientCPP_MedicationSearch_Status_CompletedCheck,
            Name: cacheStore.GetString("Completed2Label"),
            Visible: true
        };
        statuses.push(statusCompleted);

        var originRX: ICheckable = {
            Id: CheckboxTarget.Myle_PatientCPP_MedicationSearch_Origin_RxCheck.toString(),
            Checked: true,
            Target: CheckboxTarget.Myle_PatientCPP_MedicationSearch_Origin_RxCheck,
            Name: cacheStore.GetString("RxLabel"),
            Visible: true
        };
        origins.push(originRX);
        var originMyle: ICheckable = {
            Id: CheckboxTarget.Myle_PatientCPP_MedicationSearch_Origin_MyleCheck.toString(),
            Checked: true,
            Target: CheckboxTarget.Myle_PatientCPP_MedicationSearch_Origin_MyleCheck,
            Name: cacheStore.GetString("MYLELabel"),
            Visible: true
        };
        origins.push(originMyle);
        var originDSQ: ICheckable = {
            Id: CheckboxTarget.Myle_PatientCPP_MedicationSearch_Origin_DSQCheck.toString(),
            Checked: true,
            Target: CheckboxTarget.Myle_PatientCPP_MedicationSearch_Origin_DSQCheck,
            Name: "DSQ",
            Visible: true
        };
        origins.push(originDSQ);

        var showInstruction: ICheckable = {
            Id: CheckboxTarget.Myle_PatientCPP_MedicationSearch_Details_InstructionsCheck.toString(),
            Checked: true,
            Target: CheckboxTarget.Myle_PatientCPP_MedicationSearch_Details_InstructionsCheck,
            Name: cacheStore.GetString("IncludeInstructionLabel"),
            Visible: true
        };
        details.push(showInstruction);

        // var showCeased: ICheckable = {
        //    Id: CheckboxTarget.Myle_PatientCPP_MedicationSearch_Details_CeaseCheck.toString(),
        //    Checked: true,
        //    Target: CheckboxTarget.Myle_PatientCPP_MedicationSearch_Details_CeaseCheck,
        //    Name: cacheStore.GetString("CeasedLabel"),
        //    Visible: true
        // }
        // details.push(showCeased);

        var patientModel: IJPatientMedicationGridModel =
        {
            SearchText: "",
            IsSearchSettingsOpen: false,
            ShowPatientMedicationDisplaySetting: false,
            IsColumnOrderingPopupOpen: false,
            FromDate: null,
            ToDate: null,
            IsAllStatusChecked: true,
            StatusChecks: Immutable.List(statuses),
            IsAllOriginChecked: true,
            OriginChecks: Immutable.List(origins),
            IsAllDetailsChecked: true,
            DetailsChecks: Immutable.List(details),
            SortColumn: null,
            SortDirection: SortDirection.ASC
        };

        return patientModel;
    }
}

export { PatientModelHelper };
